import { Box, Button, IconButton, Modal, Typography, Avatar } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

const PostModal = ({ open, onClose, post }) => {
  if (!post) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "80vh",
          width: { xs: "92vw", sm: 520 },
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 3,
          overflowY: "auto",
          p: 2
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8, color: "black" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ pt: 4 }}>
          {Array.isArray(post.images) && post.images.length > 0 && (
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
              {post.images.map((img) => (
                <img
                  key={img._id || img.url}
                  src={img.url}
                  alt="Post"
                  style={{ width: "100%", borderRadius: 8 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const CommentsModal = ({ open, onClose, comments, loading, onDelete, onLoadMore, hasMore }) => {
  const handleScroll = (event) => {
    if (!hasMore || loading || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      onLoadMore();
    }
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "80vh",
          width: { xs: "92vw", sm: 520 },
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 3,
          overflowY: "auto",
          p: 2
        }}
        onScroll={handleScroll}
      >
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8, color: "black" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ pt: 4 }}>
          <Typography variant="h6" mb={2}>
            Comments
          </Typography>
          {loading && comments.length === 0 ? (
            <Typography variant="body2">Loading comments...</Typography>
          ) : comments.length === 0 ? (
            <Typography variant="body2">No comments available.</Typography>
          ) : (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                {comments.map((comment) => {
                  return (
                    <Box
                      key={comment._id}
                      sx={{
                        display: "flex",
                        gap: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        p: 1.5
                      }}
                    >
                      <Avatar
                        src={comment.user?.profileImage || ""}
                        alt={comment.user?.fullName || "User"}
                        sx={{ width: 36, height: 36, mt: 0.5 }}
                      />
                      <Box flex={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} mb={0.5}>
                          {comment.user?.fullName || "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {comment.text || ""}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => onDelete && onDelete(comment)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const UserPostsByUser = () => {
  const location = useLocation();
  const state = location.state || {};
  const userId = state.userId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const [selectedPost, setSelectedPost] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [activePostForComments, setActivePostForComments] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const commentsPageSize = 10;
  const [refreshPostsToggle, setRefreshPostsToggle] = useState(false);

  const handleOpenPostModal = (post) => {
    setSelectedPost(post);
    setModalOpen(true);
  };

  const handleClosePostModal = () => {
    setSelectedPost(null);
    setModalOpen(false);
  };

  const fetchComments = async (postId, page, append = false) => {
    setCommentsLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };

      const res = await axios.get(
        `${apiBaseURL}/api/v1/admin/getpostcomments/${postId}`,
        {
          headers,
          params: {
            page,
            limit: commentsPageSize
          }
        }
      );

      const data = res?.data?.data || [];
      const meta = res?.data?.meta || {};

      setComments((prev) => (append ? [...prev, ...data] : data));
      setCommentsPage(page);
      if (meta && typeof meta.currentPage === "number" && typeof meta.lastPage === "number") {
        setCommentsHasMore(meta.currentPage < meta.lastPage);
      } else {
        setCommentsHasMore(data.length === commentsPageSize);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch comments. Please try again."
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleOpenCommentsModal = async (post) => {
    if (!post?._id) return;

    setActivePostForComments(post);
    setComments([]);
    setCommentsHasMore(false);
    setCommentsModalOpen(true);
    await fetchComments(post._id, 1, false);
  };

  const handleCloseCommentsModal = () => {
    setCommentsModalOpen(false);
    setComments([]);
    setActivePostForComments(null);
    setCommentsPage(1);
    setCommentsHasMore(false);
  };

  const handleLoadMoreComments = async () => {
    if (!activePostForComments?._id || !commentsHasMore || commentsLoading) return;
    await fetchComments(activePostForComments._id, commentsPage + 1, true);
  };

  const handleDeleteComment = async (comment) => {
    if (!comment?._id) return;

    // Close comments modal so only confirmation box is visible
    setCommentsModalOpen(false);

    const result = await Swal.fire({
      title: "Are you sure you want to delete this comment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (!result.isConfirmed) {
      // If cancelled, reopen the comments modal
      setCommentsModalOpen(true);
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };

      await axios.delete(`${apiBaseURL}/api/v1/admin/deletecomment/${comment._id}`, {
        headers
      });

      // Remove comment locally
      setComments((prev) => prev.filter((c) => c._id !== comment._id));

      // Trigger posts refresh so comment count updates
      setRefreshPostsToggle((prev) => !prev);

      Swal.fire({
        icon: "success",
        title: "Comment deleted successfully.",
        timer: 1000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete comment. Please try again."
      });
      // On error, reopen modal so user can try again
      setCommentsModalOpen(true);
    }
  };

  useEffect(() => {
    if (!userId) return;

    (async () => {
      setLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": `${localStorage.getItem("token")}`
        };

        const res = await axios.get(`${apiBaseURL}/api/v1/admin/getuserposts/${userId}`, {
          headers,
          params: {
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize
          }
        });

        const posts = res?.data?.data || [];
        const meta = res?.data?.meta || {};

        const mappedRows = posts.map((post) => ({
          id: post._id,
          post
        }));

        setRows(mappedRows);
        setTotalRows(typeof meta.total === "number" ? meta.total : mappedRows.length);

        if (posts.length && posts[0].user) {
          setUserProfile({
            fullName: posts[0].user.fullName || "",
            profileImage: posts[0].user.profileImage || ""
          });
        }
      } catch (error) {
        console.error("Error fetching user posts:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch user posts. Please try again."
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, paginationModel.page, paginationModel.pageSize, refreshPostsToggle]);

  const columns = useMemo(
    () => [
      {
        field: "content",
        headerName: "Content",
        flex: 2,
        minWidth: 360,
        sortable: false,
        renderCell: (params) => {
          const content = params.row.post?.content || "";
          const short = content.length > 80 ? `${content.slice(0, 80)}...` : content || "N/A";
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {short}
            </Typography>
          );
        }
      },
      {
        field: "images",
        headerName: "Images",
        flex: 0.6,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          const count = Array.isArray(params.row.post?.images) ? params.row.post.images.length : 0;
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {count}
            </Typography>
          );
        }
      },
      {
        field: "likes",
        headerName: "Likes",
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const v = params.row.post?.likes;
          const count = typeof v === "number" ? v : Array.isArray(v) ? v.length : 0;
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {count}
            </Typography>
          );
        }
      },
      {
        field: "comments",
        headerName: "Comments",
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          const v = params.row.post?.comments;
          const count = typeof v === "number" ? v : Array.isArray(v) ? v.length : 0;
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {count}
            </Typography>
          );
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleOpenPostModal(params.row.post)}
            >
              <ImageIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleOpenCommentsModal(params.row.post)}
            >
              <ChatBubbleOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        )
      }
    ],
    []
  );

  const userNameFromState = state?.userName;
  const displayName = userProfile?.fullName || userNameFromState;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <Avatar sx={{ width: 40, height: 40 }} src={userProfile?.profileImage || ""}>
          {displayName ? displayName.charAt(0) : "U"}
        </Avatar>
        <Typography variant="h6">
          {displayName ? `Posts of ${displayName}` : "User Posts"}
        </Typography>
      </Box>
      <Box sx={{ height: 675, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          rowSelectionModel={[]}
          onRowSelectionModelChange={() => {}}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalRows}
          pageSizeOptions={[10, 20, 30, 50]}
          loading={loading}
          localeText={{ noRowsLabel: "No Posts available" }}
          sx={{
            fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
            fontWeight: 400,
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important"
            },
            "& .MuiDataGrid-columnHeader": {
              borderRight: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "#fff",
              "&:nth-of-type(odd)": {
                backgroundColor: "#f9f9f9"
              }
            },
            "& .Mui-selected": {
              backgroundColor: "inherit !important"
            },
            "& .MuiDataGrid-columnHeader": {
              "& .MuiDataGrid-iconButtonContainer": {
                display: "none"
              },
              "& .MuiDataGrid-menuIcon": {
                display: "none"
              }
            }
          }}
        />

        <PostModal open={modalOpen} onClose={handleClosePostModal} post={selectedPost} />
        <CommentsModal
          open={commentsModalOpen}
          onClose={handleCloseCommentsModal}
          comments={comments}
          loading={commentsLoading}
          onDelete={handleDeleteComment}
          onLoadMore={handleLoadMoreComments}
          hasMore={commentsHasMore}
        />
      </Box>
    </DashboardLayout>
  );
};

export default UserPostsByUser;

